import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './ZylenLanding.css';

/** Vimeo showreel — used below hero (launch embed + secondary sections) */
const VIMEO_ID = '1133145745';
const VIMEO_HASH = '40ca5ed1cf';

const vimeoBackgroundSrc = `https://player.vimeo.com/video/${VIMEO_ID}?h=${VIMEO_HASH}&badge=0&autopause=0&autoplay=1&muted=1&loop=1&background=1&player_id=0&app_id=58479`;
const vimeoLaunchSrc = `https://player.vimeo.com/video/${VIMEO_ID}?h=${VIMEO_HASH}&badge=0&autopause=0&autoplay=1&muted=1&loop=1&player_id=0&app_id=58479`;

const MARKETPLACE_HREF = 'https://zylen.app/#/preview/marketplace/';
const YOUTUBE_HREF = 'https://www.youtube.com/@zylen_app';
const FINAL_LOGO_SRC = `${process.env.PUBLIC_URL}/landing/final-logo-white.png`;

const NAV_H = 72;
const NAV_SECTIONS = [
  { id: 'home', label: 'home' },
  { id: 'about', label: 'about' },
  { id: 'features', label: 'features' },
  { id: 'contact', label: 'contact' },
];

const YoutubeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5zm0 2c-3.866 0-7 2.134-7 4.75V22h14v-3.25C19 16.134 15.866 14 12 14z" />
  </svg>
);

function HeroVideoBackdrop() {
  return (
    <div className="zylen-hero-video-wrap" aria-hidden>
      <iframe
        title=""
        src={vimeoBackgroundSrc}
        allow="autoplay; fullscreen; picture-in-picture"
        className="zylen-hero-iframe"
      />
    </div>
  );
}

function VimeoBackdrop() {
  return (
    <div className="zylen-vimeo-bg" aria-hidden>
      <iframe
        title=""
        src={vimeoBackgroundSrc}
        allow="autoplay; fullscreen; picture-in-picture"
      />
    </div>
  );
}

function NavScrollItem({ id, label, active, onNavigate }) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(id)}
      className="zylen-nav-item"
      data-active={active ? 'true' : 'false'}
    >
      <span className="zylen-nav-item__blob" aria-hidden />
      <span className="zylen-nav-item__text">{label}</span>
      <span className="zylen-nav-item__active-line" aria-hidden />
      <span className="zylen-nav-item__track" aria-hidden>
        <span className="zylen-nav-item__runner" aria-hidden />
      </span>
    </button>
  );
}

function NavPlainLink({ href, to, external, label, className }) {
  const inner = (
    <>
      <span className="zylen-nav-item__blob" aria-hidden />
      <span className="zylen-nav-item__text">{label}</span>
      <span className="zylen-nav-item__active-line" aria-hidden />
      <span className="zylen-nav-item__track" aria-hidden>
        <span className="zylen-nav-item__runner" aria-hidden />
      </span>
    </>
  );

  if (external) {
    return (
      <a
        href={href}
        className={`zylen-nav-item ${className || ''}`.trim()}
        rel="noopener noreferrer"
      >
        {inner}
      </a>
    );
  }

  return (
    <Link to={to} className={`zylen-nav-item ${className || ''}`.trim()}>
      {inner}
    </Link>
  );
}

function NavIconLink({ to, label, children }) {
  return (
    <Link
      to={to}
      className="zylen-nav-item zylen-nav-item--icon"
      aria-label={label}
    >
      <span className="zylen-nav-item__blob" aria-hidden />
      <span className="zylen-nav-item__icon">{children}</span>
      <span className="zylen-nav-item__track" aria-hidden>
        <span className="zylen-nav-item__runner" aria-hidden />
      </span>
    </Link>
  );
}

const ZylenLanding = () => {
  const [activeNav, setActiveNav] = useState('home');
  const [headerVisible, setHeaderVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastY = useRef(0);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - NAV_H;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    const ids = NAV_SECTIONS.map((s) => s.id);
    const updateActive = () => {
      const offset = window.scrollY + NAV_H + 56;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= offset) {
          current = id;
        }
      }
      setActiveNav(current);
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);
    return () => {
      window.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
    };
  }, []);

  useEffect(() => {
    lastY.current = window.scrollY;
    const onScrollDirection = () => {
      const y = window.scrollY;
      if (y < 90) {
        setHeaderVisible(true);
      } else if (y < lastY.current) {
        setHeaderVisible(true);
      } else {
        setHeaderVisible(false);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScrollDirection, { passive: true });
    return () => window.removeEventListener('scroll', onScrollDirection);
  }, []);

  const sloganLine =
    "a home for filmmakers and creatives";
  const aboutParagraph =
    'zylen is a home for filmmakers and creatives. It is a platform designed to help people connect, collaborate, and grow within the filmmaking industry both online and in person. More than just a social platform, zylen brings together community, opportunity, and creativity into one ecosystem. Filmmakers can discover job opportunities, attend monthly events, build and host their own communities, share their work, and connect with like-minded creatives. zylen is changing the way filmmakers use social media by creating real-world and online spaces where creatives can grow their careers, friendships, and opportunities through a shared love for filmmaking and storytelling.';

  return (
    <div className="zylen-landing">
      <header
        className={`zylen-header fixed left-0 right-0 top-0 z-[100] w-full transition-[transform,background-color,border-color] duration-300 ${headerVisible ? 'translate-y-0' : '-translate-y-full'
          }`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0)',
          backgroundColor: 'rgba(5, 8, 20, 0.42)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <div className="zylen-header-row flex w-full max-w-none items-center justify-between gap-3 px-4 py-3 sm:px-6 md:px-8">
          <button
            type="button"
            onClick={() => scrollToSection('home')}
            className="flex shrink-0 items-center gap-2 border-0 bg-transparent p-0 text-left text-inherit cursor-pointer transition-opacity duration-200 hover:opacity-85"
            aria-label="zylen home"
          >
            <img
              src={FINAL_LOGO_SRC}
              alt="zylen"
              className="zylen-final-logo h-12 w-auto object-contain sm:h-14"
            />
          </button>

          <nav className="zylen-nav flex flex-wrap items-center justify-end gap-x-0.5 gap-y-1 text-[0.72rem] font-medium text-slate-100 sm:gap-x-1 sm:text-[0.83rem] md:text-[0.92rem]">
            {NAV_SECTIONS.map(({ id, label }) => (
              <NavScrollItem
                key={id}
                id={id}
                label={label}
                active={activeNav === id}
                onNavigate={scrollToSection}
              />
            ))}
            <NavPlainLink
              href={MARKETPLACE_HREF}
              external
              label="marketplace"
              className="zylen-nav-item--cta"
            />
            <NavIconLink to="/sign-in" label="login">
              <UserIcon />
            </NavIconLink>
          </nav>
          <div className="zylen-mobile-actions">
            <Link
              to="/sign-in"
              className="zylen-mobile-user"
              aria-label="login"
            >
              <UserIcon />
            </Link>
            <button
              type="button"
              className="zylen-mobile-toggle"
              aria-label="open menu"
              aria-expanded={mobileMenuOpen ? 'true' : 'false'}
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div
          className="zylen-mobile-menu"
          data-open={mobileMenuOpen ? 'true' : 'false'}
        >
          {NAV_SECTIONS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => scrollToSection(id)}
              className="zylen-mobile-link"
            >
              {label}
            </button>
          ))}
          <a
            href={MARKETPLACE_HREF}
            className="zylen-mobile-link"
            rel="noopener noreferrer"
          >
            marketplace
          </a>
        </div>
      </header>

      {/* —— 1 hero —— */}
      <section
        id="home"
        className="relative flex min-h-[100dvh] flex-col items-center justify-center px-4 pb-16 pt-[calc(5.5rem+env(safe-area-inset-top))] zylen-section-anchor"
      >
        <HeroVideoBackdrop />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050514]/45 via-[#0a1628]/25 to-[#050514]/78" />

        <div className="relative z-[2] flex w-full max-w-4xl flex-col items-center text-center">
          <img
            src={FINAL_LOGO_SRC}
            alt="zylen"
            className="zylen-final-logo zylen-fade-in mb-8 h-20 w-auto object-contain sm:h-24"
          />
          <p
            className="zylen-hero-slogan zylen-fade-in zylen-fade-in-delay-1 mb-10 max-w-[1100px] font-normal leading-snug text-white/95"
            style={{
              fontSize: 'clamp(0.9rem, 1.05vw + 0.62rem, 1.2rem)',
              lineHeight: 1.65,
            }}
          >
            {sloganLine}
          </p>
          <div className="zylen-fade-in zylen-fade-in-delay-2">
            <span className="zylen-goo-btn-wrap">
              <span className="zylen-goo-btn-blob" aria-hidden />
              <Link to="/sign-in" className="zylen-btn-teal">
                login
              </Link>
            </span>
          </div>
        </div>
      </section>

      {/* —— 2 about —— */}
      <section id="about" className="zylen-about-full zylen-section-anchor">
        <VimeoBackdrop />
        <div className="zylen-about-overlay" />
        <div className="zylen-about-content">
          <div className="zylen-about-blob" aria-hidden />
          <h2 className="zylen-blob-heading">about us</h2>
          <p className="zylen-about-paragraph">{aboutParagraph}</p>
          <button
            type="button"
            className="zylen-about-link"
            onClick={() => scrollToSection('our-story')}
          >
            read the full story
          </button>
          <span className="zylen-goo-btn-wrap">
            <span className="zylen-goo-btn-blob" aria-hidden />
            <button
              type="button"
              className="zylen-btn-teal"
              onClick={() => scrollToSection('cta-login')}
            >
              join now!
            </button>
          </span>
        </div>
      </section>

      {/* —— 3 launch video —— */}
      <section className="relative zylen-section-anchor bg-white px-4 py-20 md:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="relative mx-auto w-full px-1">
            <div className="zylen-launch-blob" aria-hidden />
            <div className="zylen-launch-frame">
              <iframe
                title="zylen launch video"
                src={vimeoLaunchSrc}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          <p className="mt-10 text-center text-sm font-normal tracking-wide text-slate-600 md:text-base">
            zylen | a home for filmmakers and creatives
          </p>
        </div>
      </section>

      {/* —— 4 features —— */}
      <section
        id="features"
        className="zylen-section-anchor px-4 py-20 md:py-24"
        style={{ background: '#327691' }}
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-3 flex justify-center">
            <h2 className="zylen-features-title text-center font-semibold tracking-[0.2em] text-white/90">
              features
            </h2>
          </div>
          <p className="mb-14 text-center text-lg font-normal text-white md:text-xl">
            on the app
          </p>
          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {[
              {
                line: 'build real communities',
                hint: 'one-way community chat',
                image: `${process.env.PUBLIC_URL}/landing/build-real-communities.png`,
              },
              {
                line: 'meet like minded people',
                hint: 'dms & group chats',
                image: `${process.env.PUBLIC_URL}/landing/meet-like-minded.png`,
              },
              {
                line: 'sell digital products',
                hint: 'digital store',
                image: `${process.env.PUBLIC_URL}/landing/sell-digital-products.png`,
              },
              {
                line: 'more coming soon',
                hint: 'more modes on the way',
                image: `${process.env.PUBLIC_URL}/landing/more-coming-soon.png`,
              },
            ].map(({ line, hint, image }) => (
              <div
                key={line}
                className="flex flex-col items-center text-center"
              >
                <div className="zylen-feature-shot w-full max-w-[300px] transition-opacity duration-300 hover:opacity-95">
                  <img
                    src={image}
                    alt={line}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <p className="mt-4 text-sm font-medium text-white">{line}</p>
                <p className="mt-1 text-xs font-normal text-white/65">{hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* —— 5 cta / download —— */}
      <section
        id="cta-login"
        className="relative flex min-h-[85dvh] items-center justify-center px-4 py-24 zylen-section-anchor"
      >
        <VimeoBackdrop />
        <div className="absolute inset-0 bg-[#0a1628]/72" />
        <div className="relative z-[2] flex max-w-xl flex-col items-center text-center">
          <p className="mb-10 text-base font-normal leading-relaxed text-slate-100 sm:text-lg">
            start building communities, finding jobs, meeting like-minded people and selling your digital products today!
          </p>
          <span className="zylen-goo-btn-wrap">
            <span className="zylen-goo-btn-blob" aria-hidden />
            <Link to="/sign-in" className="zylen-btn-teal">
              login
            </Link>
          </span>
        </div>
      </section>

      {/* —— 5b our story —— */}
      <section
        id="our-story"
        className="zylen-section-anchor zylen-story-section px-4 py-20 md:py-28"
      >
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex justify-center">
            <h2 className="zylen-blob-heading">Full story</h2>
          </div>
          <div className="zylen-story-body">
            <p>
              at the beginning of 2025, joshua farrer directed his focus onto building a community online and creating a space where people could interact and learn from each other. beyond this, he envisioned a space that would allow him to sell his digital products to this community of individuals, helping them learn and grow within the filmmaking sphere.
            </p>
            <p>
              joshua embarked on this journey with a talented group of web designers who helped him design his first website, bringing his vision to life. during the building of joshua’s website, web designer and good friend of joshua’s, retief, suggested building an app for “joshua farrer’s” community and digital products. loving this idea, joshua gave the go-ahead to start planning and developing this app.
            </p>
            <p>
              during the planning process, joshua realized the true potential this app held. not only for himself, but for filmmakers, content creators and individuals across the entire film industry in advancing their personal brands, communities and digital products.
            </p>
            <p>
              joshua recognized the truth that social media is no longer authentically “social.” he observed individuals spending their time “doom-scrolling” online, wasting away hours of their day, over actually meeting new people, forming genuine connections and learning from one another.
            </p>
            <p>this is where the idea of zylen was born.</p>
            <p>
              the name surfaced after a few weeks of brainstorming. joshua and his brother, michael, eventually came up with “zylen.” joshua wanted the name to be something new. something not overused. something that would grab people’s attention. the name “zylen” has no specific meaning. however, joshua sought out to give it meaning.
            </p>
            <p>
              with the app zylen now built, joshua’s vision is to bring the “social” back into “social media” by assisting in the building of real communities, meeting like-minded people online and in person, helping individuals discover jobs and opportunities through the zylen job board, and allowing users to sell digital products through the app.
            </p>
            <p>
              beyond the digital world, through zylen’s monthly in-person events, filmmakers, creatives and individuals across the film industry are able to connect face-to-face, build genuine friendships and create meaningful real-world connections.
            </p>
            <p>
              joshua wants this platform to bring you new opportunities. to help you make new friends. to allow the growth of your business. to assist you in learning and making money online. ultimately, his vision is that zylen would remind you to savor the “now” of life, by being intentional with those who are existing at the same time as you.
            </p>
          </div>
        </div>
      </section>

      {/* —— 6 testimonials —— */}
      <section
        className="zylen-testimonials zylen-section-anchor px-4 py-20 md:py-28"
        style={{
          '--testimonial-blob': `url(${process.env.PUBLIC_URL}/landing/testimonial-blob.png)`,
        }}
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-3 flex justify-center">
            <h2 className="zylen-blob-heading text-center tracking-[0.2em] text-black">
              voices
            </h2>
          </div>
          <p className="mb-14 text-center text-lg font-normal text-black md:text-xl">
            from early members
          </p>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {[
              {
                quote:
                  'zylen FINALLY gave me a platform to connect authentically with like minded people!!',
                name: 'Daniah de Villiers',
              },
              {
                quote:
                  'Wow! I absolutely love the interface and the way I can create communities! So easy to connect with people. 100% a MUST download',
                name: 'Stiaan van Huyssteen',
              },
              {
                quote:
                  'zylen transformed my business by expanding my network. Highly recommend',
                name: 'Marko Prinsloo',
              },
              {
                quote:
                  'What an easy way to sell my course, ‘Bride To Be’. Love this app!',
                name: 'Ghia Hough',
              },
            ].map(({ quote, name }) => (
              <article key={name} className="zylen-quote-card">
                <div className="zylen-quote-accent" aria-hidden />
                <div className="zylen-quote-content pl-3.5">
                  <div className="zylen-quote-mark">&ldquo;</div>
                  <p className="zylen-quote-text">{quote}</p>
                  <p className="zylen-quote-stars" aria-label="5 star rating">
                    ★★★★★
                  </p>
                  <p className="zylen-quote-author">{name}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* —— 9 contact —— */}
      <section
        id="contact"
        className="zylen-contact-section zylen-section-anchor"
      >
        <div className="zylen-contact-card">
          <h2>get in touch</h2>
          <p>
            let&apos;s connect! whether it&apos;s feedback, collaboration, or
            support — we&apos;re here to help.
          </p>
          <a href="mailto:contact@zylen.app" className="zylen-contact-email">
            contact@zylen.app
          </a>
          <a
            href={YOUTUBE_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="zylen-contact-yt"
            aria-label="zylen on youtube"
          >
            <YoutubeIcon />
          </a>
        </div>
      </section>

      {/* —— 10 cta / download (repeat) —— */}
      <section className="relative flex min-h-[45dvh] items-center justify-center px-4 py-24 zylen-section-anchor">
        <VimeoBackdrop />
        <div className="absolute inset-0 bg-[#0a1628]/72" />
        <div className="relative z-[2] flex max-w-xl flex-col items-center text-center">
          <p className="mb-10 text-base font-normal leading-relaxed text-slate-100 sm:text-lg">
            start building communities, finding jobs, meeting like-minded people and selling your digital products today!
          </p>
          <span className="zylen-goo-btn-wrap">
            <span className="zylen-goo-btn-blob" aria-hidden />
            <Link to="/sign-in" className="zylen-btn-teal">
              login
            </Link>
          </span>
        </div>
      </section>

      {/* —— footer —— */}
      <footer className="zylen-site-footer w-full">
        <div className="zylen-site-footer__brand">
          <img src={FINAL_LOGO_SRC} alt="zylen" className="zylen-final-logo" />
        </div>
        <hr className="zylen-site-footer__rule" />
        <p className="zylen-site-footer__legal">
          Copyright © Zylen | Powered by Zylen
        </p>
      </footer>
    </div>
  );
};

export default ZylenLanding;
